import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Alert, Button, Card, Chip, Label, Switch } from '@heroui/react'
import { Chrome } from '../ui/Chrome'
import { Icon, type IconName } from '../ui/icons'
import { QueueAlertBanner } from '../ui/QueueAlertBanner'
import { RequestBanner } from '../ui/RequestBanner'
import { useStaffText } from '../ui/StaffText'
import { api, ApiError } from '../lib/api'
import { secondsSince, useNow } from '../lib/clock'
import { controlText, pausedBusyKey, verbControlId } from '../lib/askBusy'
import { deskOffers, offeredTargets } from '../lib/deskVerbs'
import { mayChangeProfile, profileNotice } from '../lib/profileSwitch'
import { offersBackToQueue } from '../lib/backToQueue'
import { bookedMark } from '../lib/bookings'
import { byCallingOrder, callingWaitSeconds, pastTarget } from '../lib/callingWait'
import { closingSendsToQueue, returnedFirst, returnsToName, transferBody } from '../lib/cashier'
import { clock, declinedLine, declinedShown, pausedDetail, pausedHeadline } from '../lib/deskStatus'
import { deskToOffer, deskToShow, signedOutElsewhere } from '../lib/floorBoard'
import { explain } from '../lib/staffErrors'
import { formatDuration, formatMinuteLimit, nameIn } from '../lib/staffText'
import { emptyCallKey, nextCheckDue, reachableHere, waitingHere, type NextCheckAsked } from '../lib/nextHere'
import { offersStandBy, standingFor } from '../lib/standBy'
import { callableFromPool, heldFor, isHeld, offeredParkScopes, parkedPool, sentTo, ticketAt, waitedSeconds, waitingIn } from '../lib/branchState'
import { closeLabel, nextServiceSentence, nextStop, serviceLabel } from '../lib/plan'
import { createIntents } from '../lib/intents'
import { overServiceLimit, serviceLimitSec } from '../lib/serviceTime'
import { optionOn } from '../lib/orgOptions'
import { readsDeclarations } from '../lib/ticketFields'
import { colleagueChoices, deskChoices, type Colleague } from '../lib/transfer'
import { transferredFromName } from '../lib/transferredFrom'
import { useBranchLive } from '../lib/useBranch'
import { AWAIT_MS, calledTicketId, cardTicket, holdAfter, holdDims, type Hold } from '../lib/counterHold'
import { blinks, type Seen } from '../lib/counterBlink'
import type {
  BreakReason,
  CounterPolicy,
  CounterView,
  DeliveredEntry,
  DeliveredService,
  DeskStatus,
  Identity,
  ParkScope,
  QueueView,
  RequestKind,
  Service,
  Ticket,
  VisitField,
  WorkProfile,
} from '../lib/types'
import { NoteHistory } from '../ui/NoteHistory'
import { CardHead, FactsGrid, ReadyCard, cardNumber, freeCardKind, priorityName, serviceTileLetter, tileOf } from './counterCard'
import {
  AsidePanel,
  BreakMenu,
  breakChoices,
  type BreakChoice,
  CloseDialog,
  DeliveredPanel,
  OutOfTurnDialog,
  ProfileDialog,
  TransferPanel,
  WalkIn,
  type Placed,
} from './counterDialogs'
import { OpenCounter, runningProfileOf, seesAllDesks } from './counterOpen'
import { FromChip, QueueInfo, splitNumber } from './counterQueue'
import { CustomerPanel } from './counterCustomer'
import '../styles/counter.css'
import '../styles/equipment.css'

/**
 * The counter application.
 *
 * One operator, one desk, one customer at a time. The screen answers three
 * questions without being asked: who is in front of me, what can I do with them,
 * and how long is the line behind them.
 *
 * Which actions are offered comes from the ticket's state rather than a fixed row
 * with some greyed out. The server refuses an illegal action anyway; offering it
 * and then refusing teaches an operator that the buttons cannot be trusted. The
 * verbs sit in the dock under the customer, one primary; a form about the customer
 * (where to send them) opens inline under the dock. End just ends: nothing is asked.
 */

/** Held for this desk or the person at it: never out of turn (the server's queue.HeldFor). */
function ownPool(t: Ticket, counterId: string, staffId: string | undefined): boolean {
  return heldFor(t, counterId, staffId)
}

type Panel = null | 'transfer' | 'aside' | 'walk' | 'profile' | 'customer'

/** A verb in the dock: `main` is the one primary button, `miss` draws No-show soft red. */
type Verb = {
  id: string
  label: string
  go: () => unknown
  main?: boolean
  miss?: boolean
  icon?: IconName
  /** Drawn at the far end of the row (End). */
  end?: boolean
}

/** A second Recall press this soon after the last is a double press, silently dropped. */
const RECALL_GAP_MS = 1500

/** Auto-call next is this viewer's own convenience, kept in the browser. */
const AUTO_CALL_KEY = 'qms.counter.autoCall'

/** The reasons screens and the server write on their own. A park reason a clerk
 *  typed is shown in the On hold list; these are the record's words, in English
 *  whatever the reader's language, and not something to read at a desk. Written
 *  one to a line as `{ reason }` so the English guard reads them as what they are. */
const OWN_REASONS = new Set(
  [
    { reason: 'waiting on the customer' },
    { reason: 'sent to a colleague' },
    { reason: 'sent to another desk' },
    { reason: 'transferred' },
    { reason: 'the desk was closed while this ticket was in service' },
  ].map((r) => r.reason),
)
const own = (reason: string) => (OWN_REASONS.add(reason), reason)

export function Counter({
  identity,
  onNavigate,
  onSignOut,
}: {
  identity: Identity
  onNavigate: (to: string) => void
  onSignOut: () => void
}) {
  // The whole identity rather than the person, because one control on this screen
  // turns on a permission, and permissions come with the identity: the server
  // said what this person may do.
  const staff = identity.staff
  const permissions = identity.permissions
  const { lang, say } = useStaffText()
  const live = useBranchLive('counter', { lang })
  const now = useNow()
  const [counterId, setCounterId] = useState(() => window.localStorage.getItem('qms.counter') ?? '')
  const [profiles, setProfiles] = useState<WorkProfile[]>([])
  const [profileId, setProfileId] = useState('')
  const [services, setServices] = useState<Service[]>([])
  const [error, setError] = useState('')
  const [note, setNote] = useState('')
  // A note that stops the person (a refused switch, a profile changed from the Floor) is a warning.
  const [warnNote, setWarnNote] = useState('')
  const [running, setRunning] = useState(false)
  // What the card keeps drawing while this screen's own command runs (lib/counterHold).
  const [hold, setHold] = useState<Hold | null>(null)
  const busy = running || hold !== null
  // The control that started the running action, so it can say what is happening.
  const [pressed, setPressed] = useState<{ id: string; text: string } | null>(null)
  // Whether this screen is closing its own desk right now, so the effect that
  // notices a desk close under the clerk does not speak up for this one.
  const closingHere = useRef(false)
  const [panel, setPanel] = useState<Panel>(null)
  // The organisation's break reasons and desk rules (contracts/counter.md); null
  // where the server has none to give, and then the kinds and today's rules hold.
  const [breakReasons, setBreakReasons] = useState<BreakReason[] | null>(null)
  const [policy, setPolicy] = useState<CounterPolicy | null>(null)
  // Throwing the rest of the visit away is asked first: it deletes work somebody
  // asked for.
  const [dropping, setDropping] = useState(false)
  // Closing the desk with a customer at it asks first, and says what happens to them.
  const [confirmClose, setConfirmClose] = useState(false)
  const [colleagues, setColleagues] = useState<Colleague[]>([])
  const [deliveredOffered, setDeliveredOffered] = useState<DeliveredService[]>([])
  const [declared, setDeclared] = useState<VisitField[]>([])

  const branchId = live.branch?.id
  const tenantId = live.branch?.tenantId
  const zone = live.branch?.timezone
  // See lib/ticketFields: GET /fields needs ticket.read; without it the facts row
  // simply has no declared fields rather than a red line.
  // Break reasons are set in Options by branch.manage across the organisation.
  const mayBreakReasons = permissions.includes('branch.manage') && (staff.owner || (staff.branchIds ?? []).length === 0)
  const outOfTurnOn = optionOn(identity, 'outOfTurnCall')
  const mayReadDeclarations = readsDeclarations(permissions)
  // The desk this person is signed in at is theirs to come back to from any
  // browser: the remembered one is only this browser's, and the last account to
  // open a desk here overwrites it.
  const shownDesk = live.state ? deskToShow(live.state.counters, counterId, staff.id) : counterId
  useEffect(() => {
    if (shownDesk === counterId) return
    setCounterId(shownDesk)
    try {
      window.localStorage.setItem('qms.counter', shownDesk)
    } catch {
      // Remembering is a convenience; the desk is found again next time anyway.
    }
  }, [shownDesk, counterId])
  const desk = live.state?.counters.find((c) => c.counterId === shownDesk)
  // A desk that closes under the clerk (freed from the Floor, or the day ended)
  // drops them onto "Open a desk", which on its own reads as the screen having
  // forgotten them. Said once, until they open a desk again.
  const [signedOut, setSignedOut] = useState('')
  const lastDesk = useRef<CounterView | undefined>(undefined)
  const lastBranch = useRef(branchId)
  useEffect(() => {
    const sameBranch = lastBranch.current === branchId
    lastBranch.current = branchId
    const said = signedOutElsewhere(lastDesk.current, desk, staff.id, closingHere.current, lang, sameBranch)
    if (said) setSignedOut(said)
    if (desk && desk.state !== 'closed' && desk.staffId === staff.id) {
      setSignedOut('')
      closingHere.current = false
    }
    lastDesk.current = desk
  }, [desk, staff.id, lang, branchId])
  // A profile somebody else switched this desk to is said once; this screen's own
  // switch (keyed by the profile it asked for) is not.
  const ownSwitch = useRef('')
  const lastProfile = useRef<{ workProfileId?: string; sessionId?: string } | undefined>(undefined)
  useEffect(() => {
    const prev = lastProfile.current
    lastProfile.current = desk && { workProfileId: desk.workProfileId, sessionId: desk.sessionId }
    if (!desk || prev?.workProfileId === desk.workProfileId) return
    if (ownSwitch.current && ownSwitch.current === desk.workProfileId) {
      ownSwitch.current = ''
      return
    }
    const text = profileNotice(prev, desk, staff.id, lang)
    if (text) {
      setNote(text)
      setWarnNote(text)
    }
  }, [desk, staff.id, lang])
  // The profile this clerk's desk last ran (a Change included) is offered first on reopening.
  const runningProfile = runningProfileOf(desk, staff.id)
  useEffect(() => {
    if (runningProfile) setProfileId(runningProfile)
  }, [runningProfile])
  // Whether this desk is waiting for its next customer, and for how long.
  const standingSec = standingFor(desk, now)
  const standing = standingSec !== undefined
  // Bumped by a publish; the key on everything loaded over HTTP.
  const configVersion = live.state?.configVersion ?? 0
  // `services` is what this branch routes, and is what every picker offers.
  // `named` is every service in the organisation, and labels a ticket already in
  // the building whose service has since stopped routing here.
  const named = live.state?.services ?? services

  const liveCurrent = live.state && counterId ? ticketAt(live.state, counterId) : undefined
  // The card's visit: the live one, or the one a running command holds over a gap.
  const current = cardTicket(liveCurrent, hold)
  const shownRef = useRef(current)
  shownRef.current = current
  const liveRef = useRef(liveCurrent)
  liveRef.current = liveCurrent
  useEffect(() => {
    if (hold?.awaiting && liveCurrent?.id === hold.awaiting) setHold(null)
  }, [hold, liveCurrent?.id])
  useEffect(() => {
    if (!hold?.awaiting) return
    const id = window.setTimeout(() => setHold(null), AWAIT_MS)
    return () => window.clearTimeout(id)
  }, [hold?.awaiting])
  // The number blinks for a visit newly at the desk or a recall; each blink restarts it.
  const [blink, setBlink] = useState<{ id: string; n: number }>()
  const seenNumber = useRef<Seen | null>(null)
  const loaded = live.state !== null
  useEffect(() => {
    if (!loaded) return
    const next: Seen = { desk: counterId, id: current?.id, recalls: current?.recallCount ?? 0 }
    const prev = seenNumber.current
    seenNumber.current = next
    if (next.id && blinks(prev, next)) setBlink((b) => ({ id: next.id as string, n: (b?.n ?? 0) + 1 }))
  }, [loaded, counterId, current?.id, current?.recallCount])
  // The decline held when a customer last came to this desk: no longer news after that.
  const [declineActedOn, setDeclineActedOn] = useState<string>()
  const declinedAt = desk?.declined?.decidedAt
  useEffect(() => {
    if (current && declinedAt) setDeclineActedOn(declinedAt)
  }, [current, declinedAt])
  const currentServiceId = current?.transactions[current.currentTransaction]?.serviceId ?? ''

  // What may be recorded as delivered, replaced the moment the service changes so
  // the last service's list never sits under this one's customer. A service read
  // before is drawn at once, so the card does not grow a moment after a swap.
  const deliveredSeen = useRef(new Map<string, DeliveredService[]>())
  useEffect(() => {
    const seenKey = `${currentServiceId}@${configVersion}`
    setDeliveredOffered(deliveredSeen.current.get(seenKey) ?? [])
    if (!currentServiceId) return
    let stale = false
    api
      .serviceDelivered(currentServiceId)
      .then((r) => {
        deliveredSeen.current.set(seenKey, r.deliveredServices ?? [])
        if (!stale) setDeliveredOffered(r.deliveredServices ?? [])
      })
      .catch(() => {
        if (!stale) setDeliveredOffered([])
      })
    return () => {
      stale = true
    }
  }, [currentServiceId, configVersion])

  // Every open question belongs to one piece of work: keyed on the ticket and the
  // transaction within it, since a transferred visit is a second piece of work.
  const currentTxKey = `${current?.id ?? ''}#${current?.currentTransaction ?? ''}`
  useEffect(() => {
    setDropping(false)
    setPanel((p) => (p === 'walk' ? p : null))
    setConfirmClose(false)
  }, [currentTxKey])

  useEffect(() => {
    if (!branchId) return
    api
      .profiles(branchId)
      .then((r) => {
        // Defended against null as well as empty: a page left open across a
        // deployment may meet a server of another age, and a throw in a render is
        // a blank screen at a desk with a customer in front of it.
        // profileId stays the one opened with here: the wizard preselects only that or a lone profile.
        setProfiles(r.profiles ?? [])
      })
      .catch((err: Error) => setError(err.message))
    api
      .services(branchId)
      .then((r) => setServices(r.services))
      .catch(() => undefined)
    api
      .colleagues(branchId)
      .then((r) => setColleagues(r.colleagues ?? []))
      .catch(() => setColleagues([]))
    // Read again whenever somebody publishes: none of these come on the snapshot,
    // and a desk open since nine would otherwise keep the profile it loaded then.
  }, [branchId, configVersion])

  useEffect(() => {
    if (!tenantId) return
    let stillHere = true
    api
      .breakReasons()
      .then((r) => stillHere && setBreakReasons(r.reasons ?? null))
      .catch(() => stillHere && setBreakReasons(null))
    api
      .counterPolicy()
      .then((r) => stillHere && setPolicy(r.policy))
      .catch(() => stillHere && setPolicy(null))
    return () => {
      stillHere = false
    }
  }, [tenantId])

  // The declarations belong to the branch's organisation, read once per
  // organisation; a read this desk cannot make leaves the facts without them.
  useEffect(() => {
    if (!mayReadDeclarations || !branchId) return
    let stillHere = true
    api
      .declaredFields()
      .then((r) => stillHere && setDeclared(r.fields))
      .catch(() => undefined)
    return () => {
      stillHere = false
    }
  }, [mayReadDeclarations, branchId, tenantId])

  // A booked customer's own name, read once for the ticket at this desk: greeted by it.
  const [bookedName, setBookedName] = useState<{ ticketId: string; name: string } | null>(null)
  const bookedTicket = current?.appointmentId ? current.id : ''
  useEffect(() => {
    if (!bookedTicket || !branchId) return
    let stillHere = true
    api
      .bookedName(branchId, bookedTicket)
      .then((r) => stillHere && setBookedName({ ticketId: bookedTicket, name: r.name }))
      .catch(() => undefined)
    return () => {
      stillHere = false
    }
  }, [bookedTicket, branchId])

  // How many Call next could see, read by run() when the server answers an empty queue.
  const waitingCount = useRef(0)

  /** Runs one action, and says whether it worked, so a question on screen can
   *  stay there when the server refuses. */
  const run = useCallback(async (what: string, action: () => Promise<unknown>, opts?: { switching?: boolean }) => {
    setRunning(true)
    setHold({ ticket: shownRef.current, dim: Boolean(opts?.switching) })
    setError('')
    setNote('')
    let awaiting: string | undefined
    try {
      awaiting = calledTicketId(await action(), counterId)
      return true
    } catch (err) {
      // An empty queue is not a failure, and red all morning teaches an operator
      // to ignore red.
      if (err instanceof ApiError && err.isEmptyQueue) setNote(say(emptyCallKey(waitingCount.current)))
      else setError(err instanceof ApiError ? explain(err, lang, 'common.unreachable') : say('counter.failed', { what, error: String(err) }))
      return false
    } finally {
      setRunning(false)
      setPressed(null)
      setHold((h) => holdAfter(h, awaiting, liveRef.current))
    }
  }, [lang, say, counterId])

  /** Sending on, from the Transfer panel. Nothing is sent
   *  for the default placement, and returnToMe only when it was asked for. */
  const sendOn = useCallback(
    (serviceId: string, placed: Placed, returnToMe: boolean) => {
      const t = current
      if (!t) return
      void run(say('counter.busy.transferring'), async () => {
        await api.ticketAction(t.id, 'transfer', transferBody(serviceId, '', placed, [], own('transferred'), returnToMe))
        setPanel(null)
      })
    },
    [current, run, say],
  )

  /** End: the work ends, nothing is asked. */
  const close = useCallback(
    (ticketId: string) => run(say('counter.busy.closing'), () => api.ticketAction(ticketId, 'close')),
    [run, say],
  )

  /** Everything the visit still promised, thrown away. Asked first; the engine
   *  records what was dropped. */
  const dropPlan = useCallback(() => {
    const t = current
    if (!t) return
    void run(say('counter.busy.clearing'), async () => {
      await api.ticketPlan(t.id, { drop: true })
      setDropping(false)
    })
  }, [current, run, say])

  /** Don't send back: the customer owes a return and this desk decides they will
   *  not go back. The next close ends the visit or advances its plan as usual. */
  const dropReturn = useCallback(() => {
    const t = current
    if (!t) return
    void run(say('counter.busy.droppingReturn'), () => api.ticketAction(t.id, 'drop-return'))
  }, [current, run, say])

  const deskProfile = useMemo(
    () => profiles.find((p) => p.id === desk?.workProfileId),
    [profiles, desk?.workProfileId],
  )
  // The queues this desk calls: picked at the desk (simple mode) or its profile's.
  const deskQueueIds = useMemo(
    () => (desk?.queueIds && desk.queueIds.length > 0 ? desk.queueIds : (deskProfile?.queueIds ?? [])),
    [desk?.queueIds, deskProfile],
  )

  const line = useMemo(() => {
    const state = live.state
    if (!state || deskQueueIds.length === 0) return []
    // Ordered and labelled by the calling clock. Who is next is the server's to
    // say (desk.nextTicketId), not this order's.
    return byCallingOrder(
      deskQueueIds.flatMap((queueId) => waitingIn(state, queueId, now)),
      now,
      state.bookedFirst ?? true,
    )
  }, [live.state, deskQueueIds, now])

  // The customer Call next would take, as the server chose them. Absent means the
  // server named nobody, and then nobody is marked. See lib/nextHere.
  const nextId = desk?.nextTicketId

  // A hold or cooldown running out can change who is next with nothing
  // published; when the desk's view says that moment has passed, ask again.
  const askedNextCheck = useRef<NextCheckAsked | undefined>(undefined)
  const { resync } = live
  useEffect(() => {
    if (!nextCheckDue(desk, now, askedNextCheck.current)) return
    askedNextCheck.current = { at: desk!.nextCheckAt!, sentAt: now }
    resync()
  }, [desk, now, resync])

  // The line as this desk can call it, and those a clerk's delay still holds back:
  // not next and not counted, but "where is A-042" has an answer.
  const callable = useMemo(() => line.filter((t) => !isHeld(t, now)), [line, now])
  const heldBack = useMemo(() => line.filter((t) => isHeld(t, now)), [line, now])

  // A simple desk draws its own desk pool (server: queue.SimpleProfile), so Hold is Back to me.
  const onQueues = Boolean(desk?.queueIds && desk.queueIds.length > 0)
  const deskScopes = useMemo<ParkScope[]>(
    () => (onQueues ? ['desk'] : (deskProfile?.poolScopes ?? [])),
    [onQueues, deskProfile],
  )

  // What this desk may do, from the desk. The server refuses by the same list;
  // this only decides what is drawn.
  const mayIssue = permissions.includes('ticket.issue')
  const offers = useMemo(() => deskOffers(desk, mayIssue), [desk, mayIssue])
  const targets = useMemo(() => offeredTargets(offers), [offers])

  // Who is signed in at this desk: the server matches a customer waiting for a
  // person against the desk's session, not whoever is looking at the screen.
  const deskStaffId = desk?.staffId || staff.id

  // The pools this desk may park into, widest first; empty where it draws from
  // none, and then a park is what it was before pools: set aside, fetched by hand.
  const scopeChoices = useMemo(() => offeredParkScopes(deskScopes), [deskScopes])

  // The pools as this desk sees them: whom it can take back, and the rest, shown
  // anyway because a pool nobody can see is a pool nobody trusts.
  const pooled = useMemo(() => {
    const state = live.state
    if (!state) return []
    const rules = deskProfile ?? { queueIds: deskQueueIds, poolScopes: deskScopes }
    // My customers who came back first: Call next takes them first too (queue.SentTo).
    return returnedFirst(
      parkedPool(state, now).map((ticket) => {
        const mine = Boolean(counterId) && callableFromPool(ticket, rules, counterId, deskStaffId, now)
        // Held for this desk or this person: theirs to fetch with Call again,
        // whichever pool Call next draws from.
        const held = ownPool(ticket, counterId, deskStaffId)
        const sent = Boolean(counterId) && sentTo(ticket, counterId, deskStaffId)
        return { ticket, mine, sent, held, fetch: Boolean(counterId) && (mine || held) && !isHeld(ticket, now) }
      }),
    )
  }, [live.state, deskProfile, deskQueueIds, deskScopes, counterId, deskStaffId, now])

  // Everybody Call next could take here: the line, and whoever was sent or came
  // back to me. My own holds are not: only Call again fetches them.
  // Not a customer booked with a colleague: Call next leaves them to that person.
  const waiting = useMemo(
    () =>
      waitingHere(
        callable.filter((t) => reachableHere(t, deskProfile?.rules, deskStaffId)),
        pooled.filter((p) => p.mine).map((p) => p.ticket),
        nextId,
      ),
    [callable, pooled, nextId, deskProfile, deskStaffId],
  )
  waitingCount.current = waiting.length

  const planned = useMemo(() => current?.plannedServices ?? [], [current])

  // Where the next planned service happens, read off the live snapshot (the floor),
  // never the draft. Undefined before the first snapshot: no sentence is better
  // than a confident wrong one.
  const queues = live.state?.queues
  const nextStopAt = useMemo(() => {
    if (planned.length === 0 || !queues || queues.length === 0) return undefined
    return nextStop(planned[0]!, queues, deskQueueIds)
  }, [planned, queues, deskQueueIds])
  const nextSentence =
    current && planned.length > 0 && nextStopAt
      ? nextServiceSentence(current.displayNumber, serviceLabel(named, planned[0]!, lang), nextStopAt, lang)
      : ''

  const called = current?.state === 'called'
  const serving = current?.state === 'serving'
  const wrapping = current?.state === 'wrapup'
  const open = desk?.state === 'open'
  const paused = desk?.state === 'paused'
  // Read off the server's own status where it sends one; a server too old to
  // send it still gets something sensible to draw from state and the customer
  // alone, just with no kind to name.
  const status: DeskStatus = desk?.status ?? (paused ? 'break' : current ? 'serving' : 'ready')

  // What has been recorded as delivered; optional, it never holds up End.
  const entries: DeliveredEntry[] = useMemo(
    () => current?.transactions[current.currentTransaction]?.delivered ?? [],
    [current],
  )

  const deliverIntents = useRef(createIntents())
  useEffect(() => deliverIntents.current.settle(entries.map((e) => e.id)), [entries])
  const deliver = useCallback(
    async (deliveredServiceId: string, outcomeCode: string) => {
      const t = current
      if (!t) return false
      const intent = `${t.id}:${t.currentTransaction}:${deliveredServiceId}:${outcomeCode}`
      const entryId = deliverIntents.current.keyFor(intent)
      const ok = await run(say('counter.busy.recordingDelivered'), () =>
        api.deliver(t.id, entryId, deliveredServiceId, outcomeCode || undefined),
      )
      if (ok) deliverIntents.current.done(intent)
      return ok
    },
    [current, run, say],
  )
  const undeliver = useCallback(
    (entryId: string) => {
      const t = current
      if (!t) return
      void run(say('counter.busy.takingBack'), () => api.ticketAction(t.id, 'undeliver', { entryId }))
    },
    [current, run, say],
  )

  /** Setting aside. The clerk says each time who takes them back — there is no
   *  default: a narrow choice left sticky strands every later customer on one
   *  window, and a wide one made silently is a promise the clerk never read. */
  const park = useCallback(
    async (scope: ParkScope | undefined, typed: string) => {
      const t = current
      if (!t) return
      const ok = await run(say('counter.busy.parking'), () =>
        api.ticketAction(t.id, 'park', {
          ...(scope ? { scope } : {}),
          reason: typed.trim() || own('waiting on the customer'),
        }),
      )
      if (ok) setPanel(null)
    },
    [current, run, say],
  )

  /** The operator's Ask for a break, offered only with no customer at the desk.
   *  A kind the branch needs no approval for starts at once; otherwise Pending. */
  const askRequest = useCallback(
    async (kind: RequestKind) => {
      const what = say('counter.busy.asking')
      setPressed({ id: verbControlId('p'), text: what })
      await run(what, () => api.requestStatus(counterId, kind))
    },
    [counterId, run, say],
  )

  /** A break for one of the organisation's reasons; a bare kind from a server with none.
   *  The busy text shows on the Break button. */
  const askBreak = useCallback(
    async (choice: BreakChoice) => {
      if (!choice.reasonId) return askRequest(choice.kind)
      const what = say('counter.busy.asking')
      setPressed({ id: verbControlId('p'), text: what })
      await run(what, () => api.requestBreak(counterId, choice.reasonId!))
    },
    [askRequest, counterId, run, say],
  )

  /** The paused desk's one verb, its busy text on the pressed control: Cancel
   *  withdraws an undecided request, Back ends a break (no approval). */
  const pausedVerb = useCallback(
    (action: () => Promise<unknown>) => {
      const what = say(pausedBusyKey(status))
      setPressed({ id: verbControlId('p'), text: what })
      return run(what, action)
    },
    [run, say, status],
  )

  /** Close counter: signs out at once, from any state. A customer at the desk
   *  goes back in line; a running break or an undecided request ends with it. */
  const closeDesk = useCallback(async () => {
    setPressed({ id: 'close', text: say('counter.busy.closingDesk') })
    setRunning(true)
    setError('')
    setNote('')
    closingHere.current = true
    try {
      await api.closeCounter(counterId)
    } catch (err) {
      closingHere.current = false
      setError(
        err instanceof ApiError
          ? explain(err, lang, 'common.unreachable')
          : say('counter.failed', { what: say('counter.busy.closingDesk'), error: String(err) }),
      )
    }
    setRunning(false)
    setPressed(null)
    setConfirmClose(false)
  }, [counterId, lang, say])

  const serveThen = useCallback(
    async (t: Ticket, then: () => unknown) => {
      const ok = await run(say('counter.busy.serving'), () => api.ticketAction(t.id, 'serve'))
      if (ok) then()
    },
    [run, say],
  )

  // Auto-call next: each time the desk comes free (a customer gone, a break over)
  // it asks once to call the next customer, or to wait for one. A viewer's own
  // setting, so it lives in this browser.
  const [autoCall, setAutoCallState] = useState(() => {
    try {
      return window.localStorage.getItem(AUTO_CALL_KEY) === '1'
    } catch {
      return false
    }
  })
  const setAutoCall = useCallback((on: boolean) => {
    setAutoCallState(on)
    try {
      window.localStorage.setItem(AUTO_CALL_KEY, on ? '1' : '0')
    } catch {
      // A convenience: it simply starts off next time.
    }
  }, [])

  // When this browser last pressed Recall: the double-press guard, by this clock alone.
  const recallPressed = useRef(0)

  // The verbs for this moment; the buttons read this list.
  const verbs = useMemo((): Verb[] => {
    const t = current
    if (!t) {
      if (paused) {
        if (status === 'pending') {
          return [{ id: 'p', label: say('counter.cancelRequest'), main: true, icon: 'close', go: () => pausedVerb(() => api.cancelRequest(counterId)) }]
        }
        // A pause from before requests existed carries no request to end: the
        // old, bare resume is still what takes it back to Ready.
        const back = status === 'break' ? () => api.setCounterState(counterId, 'open') : () => api.endBreak(counterId)
        return [{ id: 'p', label: say('counter.backFromBreak'), main: true, icon: 'play', go: () => pausedVerb(back) }]
      }
      if (!open) return []
      const list: Verb[] = []
      const call: Verb = { id: 'n', label: say('counter.callNext'), main: true, icon: 'callIn', go: () => run(say('counter.busy.calling'), () => api.callNext(counterId)) }
      // Stopping also turns Auto-call off, or the desk would start waiting again on its own.
      if (standing) {
        list.push({
          id: 'w',
          label: say('counter.stopWaiting'),
          go: () => {
            setAutoCall(false)
            return run(say('counter.busy.stopping'), () => api.stopStandingBy(counterId))
          },
        })
        if (waiting.length > 0) list.push({ ...call, main: false })
      } else if (offersStandBy({ occupied: false, open, standing, waiting: waiting.length })) {
        list.push({ id: 'w', label: say('counter.waitForNext'), main: true, go: () => run(say('counter.busy.waiting'), () => api.standBy(counterId)) })
      } else {
        list.push(call)
      }
      if (branchId && offers.walkDirect) list.push({ id: 'i', label: say('counter.serveWalkIn'), icon: 'userPlus', go: () => setPanel('walk') })
      return list
    }
    // Recall from Called and from Serving alike (a call may start serving).
    const recall: Verb[] = offers.recall
      ? [{
          id: 'r',
          label: say('counter.recall'),
          icon: 'refresh',
          go: () => {
            const at = Date.now()
            if (at - recallPressed.current < RECALL_GAP_MS) return
            recallPressed.current = at
            return run(say('counter.busy.recalling'), () => api.ticketAction(t.id, 'recall'))
          },
        }]
      : []
    if (called) {
      return [
        { id: 's', label: say('counter.startServing'), main: true, icon: 'play', go: () => run(say('counter.busy.serving'), () => api.ticketAction(t.id, 'serve')) },
        ...recall,
        ...(offersBackToQueue(t)
          ? [{
              id: 'b',
              label: say('counter.backToQueue'),
              icon: 'undo' as const,
              go: () => run(say('counter.busy.backToQueue'), () => api.ticketAction(t.id, 'back-to-queue')),
            }]
          : []),
        { id: 'x', label: say('counter.noShow'), miss: true, icon: 'userX', go: () => run(say('counter.busy.markingNoShow'), () => api.ticketAction(t.id, 'no-show', { reason: 'did not appear' })) },
        ...(targets.length > 0 ? [{ id: 't', label: say('counter.transfer'), icon: 'swap' as const, go: () => serveThen(t, () => setPanel('transfer')) }] : []),
      ]
    }
    if (serving) {
      // Next: end this visit and call the next customer in one press.
      const next: Verb = {
        id: 'n',
        label: say('counter.next'),
        main: true,
        icon: 'callIn',
        go: () =>
          // Nobody waiting after the end is run's quiet note, not an error.
          run(
            say('counter.busy.calling'),
            async () => {
              await api.ticketAction(t.id, 'close')
              return api.callNext(counterId)
            },
            { switching: true },
          ),
      }
      return [
        next,
        ...recall,
        ...(offers.park ? [{ id: 'h', label: say('counter.park'), icon: 'pause' as const, go: () => setPanel('aside') }] : []),
        ...(targets.length > 0 ? [{ id: 't', label: say('counter.transfer'), icon: 'swap' as const, go: () => {
          setError('')
          setPanel('transfer')
        } }] : []),
        // "End" on a visit that still owes the customer something is "Next service".
        {
          id: 'f',
          label: planned.length > 0 ? closeLabel(planned, lang) : say('counter.end'),
          icon: 'stop',
          end: true,
          go: () => close(t.id),
        },
      ]
    }
    // A ticket an earlier release left in wrap-up: End finishes it.
    if (wrapping) {
      return [{ id: 'f', label: say('counter.end'), main: true, icon: 'stop', end: true, go: () => run(say('counter.busy.closing'), () => api.ticketAction(t.id, 'finish-wrapup')) }]
    }
    return []
  }, [
    current, called, serving, wrapping, open, paused, status, standing, waiting.length, counterId, branchId, setAutoCall,
    planned, run, offers, targets.length, say, lang, serveThen, close, pausedVerb,
  ])

  // Which waiting or held customer the operator is confirming a call out of turn for.
  const [oot, setOot] = useState('')
  const atDesk = Boolean(desk && desk.state !== 'closed' && desk.staffId === staff.id)

  // Each free spell (the desk free again after a customer or a break) is counted,
  // so Auto-call asks once per spell and a refusal is not retried in a loop.
  const freeSig = `${desk?.sessionId ?? ''}|${current?.id ?? ''}|${desk?.state ?? ''}`
  const [spell, setSpell] = useState(0)
  useEffect(() => setSpell((n) => n + 1), [freeSig])
  const autoTried = useRef(-1)
  useEffect(() => {
    if (!autoCall || !atDesk || !open || current || standing || busy || panel || oot || confirmClose) return
    if (autoTried.current === spell) return
    autoTried.current = spell
    void run(say('counter.busy.waiting'), () => api.standBy(counterId))
  }, [autoCall, spell, atDesk, open, current, standing, busy, panel, oot, confirmClose, counterId, run, say])
  const toggleAuto = (on: boolean) => {
    autoTried.current = -1
    setAutoCall(on)
    if (!on && standing) void run(say('counter.busy.stopping'), () => api.stopStandingBy(counterId))
  }

  const chrome = (children: ReactNode) => (
    <Chrome
      title={say('entry.app.counter')}
      staff={staff}
      branch={live.branch}
      branches={live.branches}
      status={live.status}
      onChooseBranch={live.chooseBranch}
      onNavigate={onNavigate}
      onSignOut={onSignOut}
    >
      {children}
    </Chrome>
  )

  if (!live.state) {
    return chrome(
      <main className="page page--work">
        <p className="empty">{live.error || say('counter.loadingBranch')}</p>
      </main>,
    )
  }

  // A queue drawn as the letter its customers' tickets carry.
  const letterOf = (q: QueueView | undefined) => (q ? q.letter || q.code.slice(0, 1) : '')
  const queueById = (id: string | undefined) => live.state!.queues.find((q) => q.queueId === id)
  const profileLetters = (p: WorkProfile) => p.queueIds.map((id) => letterOf(queueById(id)))
  const profileNames = (p: WorkProfile) =>
    p.queueIds.map((id) => nameIn(queueById(id)?.name, lang, queueById(id)?.code ?? '')).filter(Boolean).join(' · ')

  const breaks = breakChoices(breakReasons, lang)

  // Nobody signed in at a desk yet.
  if (!desk || desk.state === 'closed' || desk.staffId !== staff.id) {
    const offered = deskToOffer(live.state.counters, counterId)
    return chrome(
      <OpenCounter
        counters={live.state.counters}
        profiles={profiles}
        counterId={offered}
        profileId={profileId}
        seeAll={seesAllDesks(permissions)}
        onDesk={setCounterId}
        letters={profileLetters}
        names={profileNames}
        error={error}
        signedOut={signedOut}
        busy={busy}
        onCancel={() => onNavigate('/')}
        onOpen={(workProfileId) =>
          run(say('counter.busy.opening'), async () => {
            await api.openCounter(offered, workProfileId)
            setProfileId(workProfileId)
            closingHere.current = false
            setCounterId(offered)
            window.localStorage.setItem('qms.counter', offered)
          })
        }
      />,
    )
  }

  // How long the ticket has been in the state it is in: the figure that decides
  // between waiting, recalling and giving up.
  const tx = current?.transactions[current.currentTransaction]
  const inStateSince = current ? (called ? current.lastCalledAt : (tx?.startedAt ?? current.lastCalledAt)) : undefined
  const inState = secondsSince(inStateSince, now)
  // The longest wait, not the first's: somebody sent to the front heads the line
  // whatever they have waited.
  const longest = callable.reduce((most, t) => Math.max(most, callingWaitSeconds(t, now)), 0)
  const breaching = pastTarget(callable, now)
  // A pause from before requests existed has no request to read a clock from;
  // the stage still draws one from pausedSince, as it always did.
  const pausedSec = paused ? secondsSince(desk.pausedSince, now) : 0
  const legacyBreak = paused && status === 'break'
  const pausedHead = paused
    ? legacyBreak
      ? say('counter.onBreak')
      : status !== 'pending' && desk.request?.reason
        ? desk.request.reason
        : pausedHeadline(status, lang)
    : ''
  const pausedBody = paused && !legacyBreak ? pausedDetail(status, desk.request, now, lang) : undefined
  const freeKind = freeCardKind({ paused, standing, waiting: waiting.length })
  const waited = current ? waitedSeconds(current, now) : 0
  const overTarget = Boolean(current?.serviceLevelSec && waited > current.serviceLevelSec)
  // The service's own time limit, on the clock the managers are emailed by.
  const limitSec = serviceLimitSec(named, currentServiceId)
  const overLimit = overServiceLimit(current, named, now)
  const mayCall = !current && open && !busy
  const queueName = current ? nameIn(queueById(current.queueId)?.name, lang, queueById(current.queueId)?.code ?? '') : ''
  // Whom closing this desk sends to the queue, said before it is closed.
  const toQueue = closingSendsToQueue(live.state.tickets, desk, staff.id)
  const ootTicket = oot ? (callable.find((t) => t.id === oot) ?? pooled.find((p) => p.ticket.id === oot)?.ticket) : undefined

  // A desk opened on picked queues (simple mode) shows them read-only until it
  // closes; Change moves it to a profile.
  const simpleDesk = Boolean(desk.queueIds && desk.queueIds.length > 0)
  const deskProfileId = simpleDesk ? '' : (desk.workProfileId ?? '')

  const switchProfile = (workProfileId: string) => {
    if (workProfileId === deskProfileId) {
      setPanel(null)
      return
    }
    if (mayChangeProfile(desk) === 'busy') {
      setError('')
      setNote(say('eq.counter.busy'))
      setWarnNote(say('eq.counter.busy'))
      setPanel(null)
      return
    }
    ownSwitch.current = workProfileId
    void run(say('counter.workProfile'), () => api.changeSessionProfile(counterId, workProfileId)).then((ok) => {
      if (ok) setPanel(null)
      else ownSwitch.current = ''
    })
  }

  const queueNames = (ids: readonly string[]) =>
    ids.map((id) => nameIn(queueById(id)?.name, lang, queueById(id)?.code ?? '')).filter(Boolean).join(' · ')
  // A supervisor's change that lands when the visit at the desk ends.
  const pendingName = desk.pending
    ? desk.pending.workProfileId
      ? (profiles.find((p) => p.id === desk.pending!.workProfileId)?.name ?? '')
      : queueNames(desk.pending.queueIds ?? [])
    : ''
  const mayChooseWork = profiles.some((p) => p.id !== deskProfileId)

  /** A waiting or held customer chosen by hand: straight away when they are the
   *  server's next or held for this desk, otherwise confirmed out of turn first. */
  const callTicket = (t: Ticket) => {
    if (t.id === nextId || ownPool(t, counterId, deskStaffId)) {
      void run(say('counter.busy.calling'), () => api.callSpecific(counterId, t.id, ''))
      return
    }
    setOot(t.id)
  }

  const changeBusy = mayChangeProfile(desk) === 'busy'
  const closeLines = [
    ...(current ? [called ? say('counter.closingPutsBack', { number: current.displayNumber }) : say('counter.closingParks', { number: current.displayNumber })] : []),
    ...(toQueue > 0 ? [say('counter.closingSendsToQueue', { n: toQueue })] : []),
  ]
  const { letter, digits } = current ? splitNumber(current) : { letter: '', digits: '' }
  const sentBy = current ? transferredFromName(current, live.state.counters, colleagues) : null
  const shownService = current ? serviceLabel(named, currentServiceId, lang) : ''
  const booked = current ? bookedMark(current, lang, zone) : ''
  const returnsTo = current ? returnsToName(current, colleagues, live.state.counters) : ''

  // The inline form under the dock, for the customer at the desk or a walk-in.
  const inline =
    panel === 'transfer' && current ? (
      <TransferPanel
        ticket={current}
        targets={targets}
        services={services}
        queues={live.state.queues}
        desks={deskChoices(live.state.counters, counterId)}
        colleagues={colleagueChoices(colleagues, deskStaffId)}
        disabled={busy}
        error={error}
        onCancel={() => setPanel(null)}
        onTransfer={(serviceId, placed, returnToMe) => sendOn(serviceId, placed, returnToMe)}
        onSend={(request) => {
          // A desk or a colleague is a park: the same work, finished by somebody
          // else, so nothing is asked about what was done.
          if (request.action !== 'park') return
          void run(say('counter.busy.sending'), async () => {
            await api.ticketAction(current.id, 'park', {
              ...request.body,
              reason: own(request.body.scope === 'staff' ? 'sent to a colleague' : 'sent to another desk'),
            })
            setPanel(null)
          })
        }}
      />
    ) : panel === 'customer' && current && branchId ? (
      <CustomerPanel
        branchId={branchId}
        ticket={current}
        bookedName={bookedName?.ticketId === current.id ? bookedName.name : undefined}
        onClose={() => setPanel(null)}
      />
    ) : panel === 'aside' && current ? (
      <AsidePanel
        ticket={current}
        deskName={desk.name}
        queueName={nameIn(queueById(current.queueId)?.name, lang, '')}
        scopes={scopeChoices}
        disabled={busy}
        onCancel={() => setPanel(null)}
        onPark={(scope, typed) => void park(scope, typed)}
      />
    ) : panel === 'walk' && !current ? (
      <WalkIn
        services={services}
        disabled={busy}
        onCancel={() => setPanel(null)}
        onWalkIn={async (serviceId) => {
          const ok = await run(say('counter.busy.walkIn'), () => api.walkDirect(branchId!, counterId, serviceId))
          if (ok) setPanel(null)
          return ok
        }}
      />
    ) : null

  return chrome(
    <main className="page page--work cw-work">
      <header className="cw-head">
        <div className="cw-head__text">
          <h1 className="cw-head__title">{say('entry.app.counter')}</h1>
          <p className="cw-head__sub">
            <span>{desk.name}</span>
            <span>{staff.displayName}</span>
            <span title={simpleDesk ? say('counter.queues') : say('counter.callingProfile')}>
              {simpleDesk ? queueNames(deskQueueIds) : desk.profileName}
            </span>
          </p>
        </div>
        <div className="cw-head__tools">
          {pendingName && (
            <Chip size="sm" variant="soft" color="accent" title={say('counter.pendingTitle')}>
              {say('counter.pendingChange', { name: pendingName })}
            </Chip>
          )}
          <Switch size="sm" className="cw-auto" isSelected={autoCall} onChange={toggleAuto}>
            <Switch.Content>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Label>{say('counter.autoCall')}</Label>
            </Switch.Content>
          </Switch>
          {mayChooseWork && (
            // A disabled button takes no hover, so the reason is on its wrapper.
            <span className="cw-busy-why" title={changeBusy ? say('eq.counter.busy') : undefined}>
              <Button variant="ghost" size="sm" isDisabled={busy || changeBusy} onPress={() => setPanel('profile')}>
                <Icon name="swap" size={16} />
                {say('counter.change')}
              </Button>
            </span>
          )}
          {/* A paused desk's way back is the dock's primary button; Break is not offered then. */}
          {paused ? null : open && !current ? (
            <BreakMenu
              choices={breaks}
              label={controlText(pressed, busy, verbControlId('p'), say('counter.takeBreak'))}
              busy={busy}
              onAsk={(choice) => void askBreak(choice)}
              onOptions={mayBreakReasons ? () => onNavigate('/administration#options') : undefined}
            />
          ) : (
            <span className="cw-busy-why" title={current ? say('eq.counter.busy') : undefined}>
              <Button variant="secondary" size="sm" isDisabled aria-label={current ? `${say('counter.takeBreak')}, ${say('eq.counter.busy')}` : undefined}>
                <Icon name="pause" size={16} />
                {say('counter.takeBreak')}
              </Button>
            </span>
          )}
          <Button variant="ghost" size="sm" isDisabled={busy} onPress={() => setConfirmClose(true)}>
            <Icon name="signout" size={16} />
            {controlText(pressed, busy, 'close', say('counter.closeDesk'))}
          </Button>
        </div>
      </header>

      <div className="cw-notices">
        {/* The Transfer panel shows its own refusal beside the form it answers. */}
        {error && panel !== 'transfer' && (
          <Alert status="danger" role="alert">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>{error}</Alert.Title>
            </Alert.Content>
          </Alert>
        )}
        {note && (
          <Alert status={note === warnNote ? 'warning' : 'accent'} role="status">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>{note}</Alert.Title>
            </Alert.Content>
          </Alert>
        )}
        <QueueAlertBanner state={live.state} queueIds={deskQueueIds} zone={zone} />
        <RequestBanner state={live.state} permissions={permissions} viewerStaffId={staff.id} branchId={branchId} onNavigate={onNavigate} />
      </div>

      <div className="cw-layout">
        <Card
          className="cw-stage"
          role="region"
          aria-label={say('counter.atYourDesk')}
          aria-busy={busy || undefined}
          data-state={current?.state ?? (paused ? 'paused' : 'free')}
          data-holding={holdDims(hold, liveCurrent) || undefined}
        >
          {!current ? (
            <>
              <ReadyCard
                heading={freeKind === 'paused' ? pausedHead : freeKind === 'standing' ? say('counter.waitingForNext') : freeKind === 'empty' ? say('counter.nobodyWaiting') : say('counter.readyToServe')}
                line={freeKind === 'ready' ? say('counter.forYou', { n: waiting.length }) : undefined}
                pill={paused ? (status === 'pending' ? say('counter.pending') : say('counter.pill.onBreak')) : say('counter.status.ready')}
                warn={paused}
                icon={<Icon name={paused ? 'pause' : 'counter'} size={24} />}
                title={paused ? (legacyBreak ? clock(pausedSec) : pausedBody?.text || clock(pausedSec)) : undefined}
                sub={freeKind === 'standing' ? clock(standingSec ?? 0) : undefined}
                late={paused && Boolean(pausedBody?.late)}
              />
              {!paused && desk.declined && declinedShown(desk.declined, now, declineActedOn) && (
                <p className="counter-hint" data-bad="" role="status">
                  {declinedLine(desk.declined, lang)}
                </p>
              )}
              <Actions verbs={verbs} busy={busy} pressed={pressed} />
            </>
          ) : (
            <>
              <CardHead
                number={cardNumber(letter, digits, current.displayNumber)}
                blink={blink?.id === current.id ? (blink.n % 2 ? 'a' : 'b') : undefined}
                service={shownService}
                chip={priorityName(current, say) || undefined}
                sub={
                  <span data-over={overTarget || undefined}>
                    {bookedName?.ticketId === current.id
                      ? say('counter.whoWaited', { name: bookedName.name, time: formatDuration(waited, lang) })
                      : say('counter.waitedFor', { time: formatDuration(waited, lang) })}
                  </span>
                }
                status={called ? say('counter.called') : say('counter.serving')}
                tone={overLimit ? 'warn' : called ? 'call' : 'ok'}
                aside={
                  <>
                    {serving && limitSec > 0 && <span>{say('counter.target', { time: formatMinuteLimit(limitSec, lang) })}</span>}
                    {current.recallCount > 0 && <span>{say('counter.recalled', { n: current.recallCount }).replace(/^,\s*/, '')}</span>}
                    <b data-late={(called && inState >= 120) || overLimit || undefined}>{clock(inState)}</b>
                  </>
                }
                marks={
                  queueName !== shownService || booked || sentBy !== null ? (
                    <>
                      {/* The line they came through, unless it only repeats the service. */}
                      {queueName !== shownService && (
                        <Chip size="sm" variant="soft">
                          {say('counter.viaQueue', { queue: queueName })}
                        </Chip>
                      )}
                      {sentBy !== null && <FromChip by={sentBy} />}
                      {booked && (
                        <Chip size="sm" variant="soft" color="accent">
                          {booked}
                        </Chip>
                      )}
                    </>
                  ) : undefined
                }
              />
              {overLimit && <p className="counter-hint cw-over">{say('counter.overLimit', { time: formatMinuteLimit(limitSec, lang) })}</p>}

              <FactsGrid
                ticket={current}
                services={named}
                declared={declared}
                letter={serviceTileLetter(queueById(current.queueId), letter)}
                tile={tileOf(live.state.queues, current.queueId)}
                waitedSec={waited}
                bookedName={bookedName?.ticketId === current.id ? bookedName.name : undefined}
                zone={zone}
                onCustomer={branchId ? () => setPanel('customer') : undefined}
              />
              <NoteHistory ticket={current} zone={zone} />

              {/* Who this customer goes back to, and the one way to say they will not. */}
              {(called || serving) && current.return && (
                <div className="counter-row">
                  <Chip size="md" variant="soft" color="accent">
                    {returnsTo ? say('counter.returnsTo', { name: returnsTo }) : say('counter.returnsBack')}
                  </Chip>
                  {serving && targets.includes('service') && (
                    <Button variant="ghost" size="sm" isDisabled={busy} onPress={dropReturn}>
                      {say('counter.dontSendBack')}
                    </Button>
                  )}
                </div>
              )}

              {/* What this visit still owes the customer. "Just this one" sits with the
                  list it deletes, and asks first. */}
              {planned.length > 0 && (
                <div className="counter-row">
                  <span className="counter-row__label">{say('counter.stillToCome')}</span>
                  {planned.map((serviceId, i) => (
                    <Chip size="sm" variant="soft" key={`${serviceId}#${i}`}>
                      {serviceLabel(named, serviceId, lang)}
                    </Chip>
                  ))}
                  {serving &&
                    (dropping ? (
                      <>
                        <span className="counter-hint">{say('counter.dropRest')}</span>
                        <Button variant="ghost" size="sm" onPress={() => setDropping(false)}>{say('common.keep')}</Button>
                        <Button variant="danger" size="sm" isDisabled={busy} onPress={dropPlan}>{say('counter.justThisOne')}</Button>
                      </>
                    ) : (
                      <Button variant="ghost" size="sm" isDisabled={busy} onPress={() => setDropping(true)}>{say('counter.justThisOne')}</Button>
                    ))}
                </div>
              )}
              {serving && nextSentence && <p className="counter-then">{nextSentence}</p>}

              {/* What was delivered, recorded while the work goes on. */}
              {(called || serving) && (deliveredOffered.length > 0 || entries.length > 0) && (
                <DeliveredPanel
                  key={`${currentTxKey}:delivered`}
                  offered={deliveredOffered}
                  entries={entries}
                  disabled={busy}
                  onAdd={deliver}
                  onRemove={undeliver}
                />
              )}

              <Actions verbs={verbs} busy={busy} pressed={pressed} />
            </>
          )}
          {inline}
        </Card>

        <aside className="cw-side">
          <QueueInfo
            callable={callable}
            waiting={waiting.length}
            heldBack={heldBack}
            pooled={pooled}
            away={desk.away ?? []}
            outOfTurn={outOfTurnOn}
            tickets={live.state.tickets}
            nextId={nextId}
            desk={desk}
            deskProfile={deskProfile}
            occupied={Boolean(current)}
            mayCall={mayCall}
            busy={busy}
            counters={live.state.counters}
            colleagues={colleagues}
            named={named}
            permissions={permissions}
            zone={zone}
            now={now}
            longest={longest}
            breaching={breaching}
            ownReason={(r) => OWN_REASONS.has(r)}
            onCall={callTicket}
            // No queue is sent: they go back to the line they left.
            onPutBack={(t) => run(say('counter.busy.puttingBackInLine'), () => api.ticketUnpark(t.id))}
          />
        </aside>
      </div>

      {panel === 'profile' && (
        <ProfileDialog
          profiles={profiles}
          current={deskProfileId}
          letters={profileLetters}
          names={profileNames}
          busy={busy}
          onSave={switchProfile}
          onClose={() => setPanel(null)}
        />
      )}
      {ootTicket && (
        <OutOfTurnDialog
          ticket={ootTicket}
          // A server with no policy to read requires a reason, as it always has.
          reasonRequired={policy ? policy.outOfTurnReasonRequired : true}
          busy={busy}
          onClose={() => setOot('')}
          onCall={(reason) =>
            void run(say('counter.busy.calling'), async () => {
              await api.callSpecific(counterId, ootTicket.id, reason)
              setOot('')
            })
          }
        />
      )}
      {confirmClose && (
        <CloseDialog
          lines={closeLines}
          deskName={desk.name}
          busy={busy}
          label={controlText(pressed, busy, 'close', say('counter.closeDesk'))}
          onClose={() => setConfirmClose(false)}
          onConfirm={() => void closeDesk()}
        />
      )}
    </main>,
  )
}

/** The action dock under the customer: the state's one primary verb, the rest secondary, End at the far end. */
function Actions({
  verbs,
  busy,
  pressed,
}: {
  verbs: Verb[]
  busy: boolean
  pressed: { id: string; text: string } | null
}) {
  // The verb pressed last: it carries the progress while its command runs.
  const [last, setLast] = useState('')
  if (verbs.length === 0) return null
  const button = (v: Verb) => (
    <Button
      key={v.id}
      variant={v.main ? 'primary' : v.miss ? 'danger-soft' : 'secondary'}
      className={v.end ? 'cw-act cw-act--end' : 'cw-act'}
      isDisabled={busy && last !== v.id}
      isPending={busy && last === v.id}
      onPress={() => {
        setLast(v.id)
        void v.go()
      }}
    >
      {v.icon && <Icon name={v.icon} size={16} />}
      {controlText(pressed, busy, verbControlId(v.id), v.label)}
    </Button>
  )
  return (
    <div className="cw-dock">
      {verbs.filter((v) => !v.end).map(button)}
      {verbs.filter((v) => v.end).map(button)}
    </div>
  )
}
